import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CombinedEmail,
  GroupListItemResponse,
  GroupResponse,
  NewGroupRequest,
  UpdateGroupRequest,
} from '@models/notifications/receiver-groups/receiver-groups.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, map, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { ReceiverGroupsService } from './receiver-groups.service';

type ReceiverGroupsState = {
  groups: GroupListItemResponse[];
  isLoading: boolean;
  groupCreateLoading: boolean;

  selectedGroup: GroupResponse | null;
  selectedGroupLoading: boolean;

  combinedEmailCreateLoading: boolean;
  selectedCombinedEmail: CombinedEmail | null;
  selectedCombinedEmailLoading: boolean;
};

const initialState: ReceiverGroupsState = {
  groups: [],
  isLoading: false,
  groupCreateLoading: false,

  selectedGroup: null,
  selectedGroupLoading: false,

  combinedEmailCreateLoading: false,
  selectedCombinedEmail: null,
  selectedCombinedEmailLoading: false,
};

const receiverGroupsDictionary: StateDictionary<any> = {
  entityList: 'groups',
  listLoading: 'isLoading',
  selectedEntity: 'selectedGroup',
  selectedEntityLoading: 'selectedGroupLoading',
  entityId: 'id',
  createLoading: 'groupCreateLoading',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const ReceiverGroupsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('receiver-groups-store'),

  withComputed(state => ({
    selectedGroupId: computed(() => state.selectedGroup()?.id || null),
    groupCombinedEmails: computed(() => state.selectedGroup()?.combinedEmails || []),
    selectedCombinedEmailEmail: computed(() => state.selectedCombinedEmail()?.email || null),
  })),

  withMethods((store, receiverGroupsService = inject(ReceiverGroupsService), router = inject(Router)) => {
    createStateResetHandler<ReceiverGroupsState>('/notifications/groups')(store, initialState);
    createCustomStateSetOnUrlHandler<ReceiverGroupsState>('/notifications/groups', { selectedGroup: null, selectedCombinedEmail: null })(
      store,
    );
    createCustomStateSetOnRegexHandler<ReceiverGroupsState>(new RegExp(`^/notifications/groups/${guidRegex}/emails$`))(store, {
      selectedCombinedEmail: null,
    });

    const repositoryMethods = {
      getAll: getAll<GroupListItemResponse>({
        store,
        service: receiverGroupsService,
        dictionary: receiverGroupsDictionary,
      }),
      getOne: getOne<GroupResponse>({
        store,
        service: receiverGroupsService,
        dictionary: receiverGroupsDictionary,
      }),
      add: addEntity<GroupResponse, NewGroupRequest>({
        store,
        service: receiverGroupsService,
        dictionary: receiverGroupsDictionary,
        onSuccessCallback: newReceiverGroup => {
          router.navigate(['notifications', 'groups', newReceiverGroup.id, 'details']);
        },
      }),
      update: updateEntity<GroupResponse, UpdateGroupRequest>({
        store,
        service: receiverGroupsService,
        dictionary: receiverGroupsDictionary,
      }),
      delete: deleteEntity<GroupResponse>({
        store,
        service: receiverGroupsService,
        dictionary: receiverGroupsDictionary,
        onSuccessCallback: () => {
          router.navigate(['notifications', 'groups']);
        },
      }),

      addCombinedEmail: rxMethod<{ updatedGroup: UpdateGroupRequest; combinedEmail: CombinedEmail }>(
        pipe(
          tap(() => patchState(store, { combinedEmailCreateLoading: true, selectedGroupLoading: true })),
          switchMap(({ updatedGroup, combinedEmail }) =>
            receiverGroupsService.update(store.selectedGroupId()!, updatedGroup).pipe(
              tap(group => {
                patchState(store, state => ({
                  selectedGroupLoading: false,
                  combinedEmailCreateLoading: false,
                  selectedGroup: group,
                  groups: sortAlphabetically(
                    findAndReplace<GroupListItemResponse>(state.groups, group, item => item.id === group.id),
                    receiverGroupsDictionary.sortFn!,
                  ),
                }));
                router.navigate(['notifications', 'groups', group.id, 'emails', combinedEmail.email, 'details']);
              }),
              catchError(() => {
                patchState(store, { selectedGroupLoading: false, combinedEmailCreateLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      updateCombinedEmail: rxMethod<{ updatedGroup: UpdateGroupRequest; combinedEmail: CombinedEmail }>(
        pipe(
          tap(() => patchState(store, { selectedCombinedEmailLoading: true, selectedGroupLoading: true })),
          switchMap(({ updatedGroup, combinedEmail }) =>
            receiverGroupsService.update(store.selectedGroupId()!, updatedGroup).pipe(
              tap(group => {
                patchState(store, {
                  selectedGroupLoading: false,
                  selectedCombinedEmailLoading: false,
                  selectedGroup: group,
                  selectedCombinedEmail: combinedEmail,
                });
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedCombinedEmail: { ...state.selectedCombinedEmail! },
                  selectedGroupLoading: false,
                  selectedCombinedEmailLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deleteCombinedEmail: rxMethod<UpdateGroupRequest>(
        pipe(
          tap(() => patchState(store, { selectedCombinedEmailLoading: true })),
          switchMap(updatedGroup =>
            receiverGroupsService.update(store.selectedGroupId()!, updatedGroup).pipe(
              tap(group => {
                const groupId = group.id;
                patchState(store, {
                  selectedCombinedEmailLoading: false,
                  selectedCombinedEmail: null,
                  selectedGroup: group,
                });
                router.navigate(['notifications', 'groups', groupId, 'emails']);
                repositoryMethods.getAll();
              }),
              catchError(() => {
                patchState(store, { selectedCombinedEmailLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      selectCombinedEmail: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { selectedCombinedEmailLoading: true })),
          map(email => {
            const combinedEmail = store.groupCombinedEmails().find(e => e.email === email);
            if (combinedEmail) {
              patchState(store, { selectedCombinedEmailLoading: false, selectedCombinedEmail: combinedEmail });
            } else {
              patchState(store, { selectedCombinedEmailLoading: false });
            }
          }),
        ),
      ),
    };

    const navigationActions = {
      goToGroupActions: async (id: string): Promise<boolean> => {
        const success = await router.navigate(['notifications', 'groups', id]);
        if (success) {
          if (!store.selectedGroupLoading()) repositoryMethods.getOne(id);
        }
        return success;
      },
      goToCombinedEmailActions: async (id: string, email: string): Promise<boolean> => {
        const success = await router.navigate(['notifications', 'groups', id, 'emails', email]);
        if (success) {
          if (store.selectedGroupId() !== id) {
            repositoryMethods.getOne(id);
          }
          repositoryMethods.selectCombinedEmail(email);
        }
        return success;
      },
      goToCombinedEmailsListPage: (id: string): void => {
        patchState(store, { selectedCombinedEmail: null });
        router.navigate(['notifications', 'groups', id, 'emails']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface ReceiverGroupsStoreType {
  // state signals
  readonly groups: Signal<GroupListItemResponse[]>;
  readonly isLoading: Signal<boolean>;
  readonly groupCreateLoading: Signal<boolean>;
  readonly selectedGroup: Signal<GroupResponse | null>;
  readonly selectedGroupLoading: Signal<boolean>;
  readonly combinedEmailCreateLoading: Signal<boolean>;
  readonly selectedCombinedEmail: Signal<CombinedEmail | null>;
  readonly selectedCombinedEmailLoading: Signal<boolean>;

  // computed signals
  readonly selectedGroupId: Signal<string | null>;
  readonly groupCombinedEmails: Signal<CombinedEmail[]>;
  readonly selectedCombinedEmailEmail: Signal<string | null>;

  getAll: () => void;
  getOne: (id: string) => void;
  add: (request: NewGroupRequest) => void;
  update: (input: { id: string; dto: UpdateGroupRequest }) => void;
  delete: (id: string) => void;
  addCombinedEmail: (input: { updatedGroup: UpdateGroupRequest; combinedEmail: CombinedEmail }) => void;
  updateCombinedEmail: (input: { updatedGroup: UpdateGroupRequest; combinedEmail: CombinedEmail }) => void;
  deleteCombinedEmail: (updatedGroup: UpdateGroupRequest) => void;
  selectCombinedEmail: (email: string) => void;

  // navigation methods
  goToGroupActions: (id: string) => Promise<boolean>;
  goToCombinedEmailActions: (id: string, email: string) => Promise<boolean>;
  goToCombinedEmailsListPage: (id: string) => void;
}
